import React, { useState, useEffect } from 'react';
import { LOCAL_CATALOG } from './CatalogData';

export default function ProductDetail({ 
  activeProduct, 
  setCurrentPage, 
  handleProductClick, 
  addToCart, 
  cart, 
  handleSingleInquiryClick 
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [addedItems, setAddedItems] = useState({});

  // Monitor screen layout changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Guard clause if activeProduct hasn't loaded yet
  if (!activeProduct) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'system-ui, sans-serif' }}>
        <p>Loading product details...</p>
        <button onClick={() => setCurrentPage('products')} style={{ padding: '8px 16px', cursor: 'pointer' }}>
          Back to Catalog
        </button>
      </div>
    );
  }

  const isInCart = cart.some(item => item.id === activeProduct.id);

  // 🛒 Enhanced add to cart handler with animation feedback
  const handleAddToCart = (asset, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    // If already in cart, show flash feedback
    if (cart.some(item => item.id === asset.id)) {
      setAddedItems(prev => ({ ...prev, [asset.id]: 'flash' }));
      setTimeout(() => {
        setAddedItems(prev => ({ ...prev, [asset.id]: undefined }));
      }, 500);
      return;
    }
    
    // Get the button element for animation reference
    let sourceElement = null;
    if (event && event.currentTarget) {
      sourceElement = event.currentTarget;
    } else if (event && event.target) {
      sourceElement = event.target.closest('button') || event.target;
    }
    
    // Call parent addToCart with the asset and event for animation
    addToCart(asset, event || sourceElement);
    
    // Show "Added!" state
    setAddedItems(prev => ({ ...prev, [asset.id]: 'added' }));
    setTimeout(() => {
      setAddedItems(prev => ({ ...prev, [asset.id]: undefined }));
    }, 1500);
  };

  // Filter products from the SAME CATEGORY, excluding the current active product
  const relatedProducts = LOCAL_CATALOG.filter(
    item => (item.category || 'General') === (activeProduct.category || 'General') && item.id !== activeProduct.id
  ).slice(0, 4);

  return (
    <div style={{ 
      padding: isMobile ? '16px' : '40px 20px', 
      maxWidth: '1280px', 
      margin: '0 auto', 
      fontFamily: 'system-ui, sans-serif', 
      backgroundColor: '#f8fafc',
      width: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    }}>
      
      {/* BACK BUTTON */}
      <button 
        onClick={() => setCurrentPage('products')} 
        style={{ 
          padding: '8px 16px', 
          backgroundColor: 'white', 
          border: '1px solid #cbd5e1', 
          borderRadius: '6px', 
          cursor: 'pointer', 
          fontWeight: '600', 
          marginBottom: '24px',
          fontSize: isMobile ? '0.85rem' : '0.9rem',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f1f5f9';
          e.currentTarget.style.transform = 'translateX(-2px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'white';
          e.currentTarget.style.transform = 'translateX(0)';
        }}
      >
        &larr; Back to Catalog Matrix
      </button>
      
      {/* MAIN PRODUCT DETAIL */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: isMobile ? '24px' : '40px', 
        marginBottom: '60px', 
        flexDirection: isMobile ? 'column' : 'row',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        
        {/* Product Image Panel */}
        <div 
          onClick={() => setIsFullscreen(true)}
          style={{ 
            flex: isMobile ? '1 1 auto' : '1 1 400px',
            width: '100%',
            border: '1px solid #e2e8f0', 
            borderRadius: '12px', 
            padding: isMobile ? '20px' : '10px', 
            backgroundColor: '#fff',
            cursor: 'zoom-in',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            boxSizing: 'border-box',
            transition: 'box-shadow 0.3s ease'
          }}
          onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,0,0,0.08)'}
          onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
          title="Click to view fullscreen"
        >
          <img 
            src={activeProduct.image} 
            alt={activeProduct.name} 
            style={{ 
              width: '100%', 
              maxHeight: isMobile ? '250px' : '400px', 
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto'
            }} 
          />
          <div style={{ 
            position: 'absolute', 
            bottom: '12px', 
            right: '12px', 
            backgroundColor: 'rgba(15,23,42,0.6)', 
            color: '#fff', 
            padding: '4px 8px', 
            borderRadius: '4px', 
            fontSize: isMobile ? '0.65rem' : '0.75rem', 
            fontWeight: '600' 
          }}>
            ⛶ Click to Expand
          </div>

          {/* In Cart Badge for Main Product */}
          {isInCart && (
            <div style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              backgroundColor: '#22c55e',
              color: 'white',
              padding: '6px 14px',
              borderRadius: '20px',
              fontSize: '0.75rem',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 12px rgba(34,197,94,0.3)',
              animation: 'fadeIn 0.3s ease'
            }}>
              ✓ In Cart
            </div>
          )}
        </div>

        {/* Product Metadata & Actions */}
        <div style={{ 
          flex: isMobile ? '1 1 auto' : '1 1 350px', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start', 
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <span style={{ 
            fontSize: isMobile ? '0.7rem' : '0.8rem', 
            fontWeight: '700', 
            color: '#0284c7', 
            backgroundColor: 'rgba(2,132,199,0.08)', 
            padding: '4px 10px', 
            borderRadius: '4px' 
          }}>
            {activeProduct.category || 'General Equipment'}
          </span>
          <h2 style={{ 
            fontSize: isMobile ? '1.3rem' : '2.2rem', 
            margin: '10px 0 20px 0', 
            fontWeight: '800',
            wordBreak: 'break-word',
            lineHeight: '1.2'
          }}>
            {activeProduct.name}
          </h2>
          
          {/* BUTTONS SECTION */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '12px', 
            width: '100%' 
          }}>
            {/* 🛒 ADD TO CART BUTTON WITH ANIMATION */}
            <button 
              onClick={(e) => handleAddToCart(activeProduct, e)}
              style={{ 
                padding: isMobile ? '12px' : '14px', 
                backgroundColor: isInCart ? '#22c55e' : (addedItems[activeProduct.id] === 'added' ? '#22c55e' : '#0284c7'),
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                fontWeight: '700', 
                fontSize: isMobile ? '0.85rem' : '1rem', 
                cursor: isInCart ? 'default' : 'pointer',
                width: '100%',
                whiteSpace: isMobile ? 'normal' : 'nowrap',
                wordBreak: 'break-word',
                lineHeight: '1.4',
                transition: 'all 0.3s ease',
                transform: addedItems[activeProduct.id] === 'added' ? 'scale(0.98)' : 'scale(1)',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: isInCart ? '0 4px 12px rgba(34,197,94,0.3)' : '0 4px 12px rgba(2,132,199,0.3)'
              }}
            >
              {isInCart ? '✓ Added to Inquiry Cart' : (addedItems[activeProduct.id] === 'added' ? '✓ Added!' : '➕ Add to Inquiry Cart')}
              
              {/* Ripple effect for added items */}
              {addedItems[activeProduct.id] === 'added' && (
                <span style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '0',
                  height: '0',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255,255,255,0.4)',
                  transform: 'translate(-50%, -50%)',
                  animation: 'ripple 0.6s ease-out forwards'
                }} />
              )}
            </button>

            {/* INSTANT INQUIRY BUTTON */}
            <button 
              onClick={() => handleSingleInquiryClick(activeProduct)} 
              style={{ 
                padding: isMobile ? '12px' : '14px', 
                backgroundColor: '#ea4335', 
                color: 'white', 
                border: 'none', 
                borderRadius: '8px', 
                fontWeight: '700', 
                fontSize: isMobile ? '0.85rem' : '1rem', 
                cursor: 'pointer',
                width: '100%',
                whiteSpace: isMobile ? 'normal' : 'nowrap',
                wordBreak: 'break-word',
                lineHeight: '1.4',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(234,67,53,0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#d33426';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ea4335';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              ⚡ Instant Single Inquiry
            </button>
          </div>

          {/* Product ID Badge */}
          <div style={{ 
            marginTop: '20px', 
            padding: '10px 14px', 
            backgroundColor: '#f1f5f9', 
            borderRadius: '8px', 
            width: '100%',
            boxSizing: 'border-box'
          }}>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
              Product ID: <strong style={{ color: '#0f172a' }}>{activeProduct.id}</strong>
            </span>
          </div>
        </div>
      </div>

      <hr style={{ border: 'none', height: '1px', backgroundColor: '#e2e8f0', margin: '40px 0' }} />

      {/* RELATED PRODUCTS SECTION */}
      <section style={{ marginBottom: '40px', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: '20px' }}>
          <h3 style={{ fontSize: isMobile ? '1.1rem' : '1.5rem', fontWeight: '800', letterSpacing: '-0.02em', color: '#0f172a', margin: 0 }}>
            Related {activeProduct.category || 'General'} Equipment
          </h3>
          <div style={{ width: '30px', height: '3px', backgroundColor: '#0284c7', marginTop: '4px', borderRadius: '2px' }} />
        </div>
        
        {relatedProducts.length > 0 ? (
          <div style={{ 
            display: 'flex', 
            flexWrap: isMobile ? 'nowrap' : 'wrap', 
            gap: isMobile ? '12px' : '20px', 
            overflowX: isMobile ? 'auto' : 'visible',
            paddingBottom: isMobile ? '16px' : '0',
            WebkitOverflowScrolling: 'touch',
            margin: isMobile ? '0 -16px' : '0',
            paddingLeft: isMobile ? '16px' : '0',
            paddingRight: isMobile ? '16px' : '0'
          }}>
            {relatedProducts.map(asset => {
              const isRelatedInCart = cart.some(item => item.id === asset.id);
              const isCardHovered = hoveredCard === asset.id;
              const buttonState = addedItems[asset.id];

              return (
                <div 
                  key={asset.id}
                  onClick={() => {
                    handleProductClick(asset);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  onMouseEnter={() => setHoveredCard(asset.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  style={{
                    flex: isMobile ? '0 0 180px' : '0 1 calc(25% - 15px)',
                    width: isMobile ? '180px' : 'auto',
                    height: isMobile ? '260px' : '260px', 
                    maxWidth: '380px',
                    boxSizing: 'border-box',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    backgroundColor: '#ffffff',
                    position: 'relative',
                    boxShadow: isCardHovered ? '0 10px 15px -3px rgba(0,0,0,0.05)' : '0 2px 4px rgba(0,0,0,0.01)',
                    transform: (!isMobile && isCardHovered) ? 'translateY(-4px)' : 'translateY(0)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div style={{ width: '100%', height: '100%', backgroundColor: '#f8fafc', position: 'relative' }}>
                    <img 
                      src={asset.image} 
                      alt={asset.name} 
                      style={{ 
                        width: '100%', 
                        height: '160px',
                        objectFit: 'cover',
                        transform: isCardHovered ? 'scale(1.03)' : 'scale(1)',
                        transition: 'transform 0.4s ease'
                      }} 
                    />
                    
                    {/* Dark gradient overlay metadata block */}
                    <div style={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(to top, rgba(15,23,42,0.95) 0%, rgba(15,23,42,0.7) 70%, rgba(15,23,42,0) 100%)',
                      padding: isMobile ? '25px 8px 8px 8px' : '40px 12px 12px 12px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end'
                    }}>
                      <h4 style={{ 
                        margin: '0 0 8px 0', 
                        fontSize: isMobile ? '0.75rem' : '0.95rem', 
                        fontWeight: '700', 
                        lineHeight: '1.2', 
                        color: '#ffffff', 
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {asset.name}
                      </h4>

                      <div style={{ display: 'flex', gap: '4px' }}>
                        {/* 🛒 RELATED PRODUCT ADD TO CART BUTTON */}
                        <button 
                          onClick={(e) => handleAddToCart(asset, e)} 
                          style={{ 
                            flex: 1,
                            padding: isMobile ? '6px 4px' : '6px 4px', 
                            backgroundColor: isRelatedInCart ? '#22c55e' : (buttonState === 'added' ? '#22c55e' : '#efff13'),
                            color: isRelatedInCart ? '#ffffff' : (buttonState === 'added' ? '#ffffff' : '#000000'),
                            border: 'none', 
                            borderRadius: '4px', 
                            fontWeight: '700', 
                            fontSize: isMobile ? '0.65rem' : '0.75rem', 
                            cursor: isRelatedInCart ? 'default' : 'pointer',
                            transition: 'all 0.3s ease',
                            transform: buttonState === 'added' ? 'scale(0.95)' : 'scale(1)',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {isRelatedInCart ? '✓ Added' : (buttonState === 'added' ? '✓ Added!' : 'Add')}
                          
                          {/* Ripple effect for added items */}
                          {buttonState === 'added' && (
                            <span style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              width: '0',
                              height: '0',
                              borderRadius: '50%',
                              backgroundColor: 'rgba(255,255,255,0.4)',
                              transform: 'translate(-50%, -50%)',
                              animation: 'ripple 0.6s ease-out forwards'
                            }} />
                          )}
                        </button>
                        
                        {/* INQUIRE BUTTON */}
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleSingleInquiryClick(asset); }} 
                          style={{ 
                            flex: 1,
                            padding: isMobile ? '6px 4px' : '6px 4px', 
                            color: '#ffffff', 
                            border: '1px solid #ef4444', 
                            backgroundColor: '#dc2626',
                            borderRadius: '4px', 
                            fontSize: isMobile ? '0.65rem' : '0.75rem', 
                            fontWeight: '700', 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#b91c1c'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                        >
                          Inquire
                        </button>
                      </div>
                    </div>

                    <div style={{ position: 'absolute', top: '6px', left: '6px', backgroundColor: 'rgba(15,23,42,0.75)', padding: '2px 6px', borderRadius: '4px', fontSize: '0.6rem', fontWeight: '600', color: '#ffffff' }}>
                      ID: {asset.id}
                    </div>

                    {/* In Cart Badge for Related Products */}
                    {isRelatedInCart && (
                      <div style={{
                        position: 'absolute',
                        top: '6px',
                        right: '6px',
                        backgroundColor: '#22c55e',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '0.55rem',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        animation: 'fadeIn 0.3s ease'
                      }}>
                        ✓ In Cart
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p style={{ color: '#64748b', fontSize: '0.9rem', fontStyle: 'italic' }}>
            No other items found in this category.
          </p>
        )}
      </section>

      {/* FULLSCREEN IMAGE LIGHTBOX MODAL */}
      {isFullscreen && (
        <div 
          onClick={() => setIsFullscreen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'zoom-out'
          }}
        >
          <button 
            onClick={() => setIsFullscreen(false)}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'none',
              border: 'none',
              color: '#ffffff',
              fontSize: '2rem',
              fontWeight: '300',
              cursor: 'pointer',
              padding: '10px',
              transition: 'transform 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            &times; Close
          </button>
          
          <img 
            src={activeProduct.image} 
            alt={activeProduct.name} 
            style={{ 
              maxWidth: '90%', 
              maxHeight: '90%', 
              objectFit: 'contain',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              borderRadius: '8px'
            }} 
          />
        </div>
      )}

      {/* ANIMATION STYLES */}
      <style>{`
        @keyframes ripple {
          0% {
            width: 0;
            height: 0;
            opacity: 1;
          }
          100% {
            width: 200px;
            height: 200px;
            opacity: 0;
          }
        }
        
        @keyframes fadeIn {
          0% {
            opacity: 0;
            transform: scale(0.8);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>

    </div>
  );
}
